import React from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Plus, Trash, CaretDown } from "@phosphor-icons/react";

const EMPTY_DETAIL = {
  hero_h1: "",
  hero_desc: "",
  cta_primary: "",
  cta_secondary: "",
  overview_heading: "",
  overview_body: "",
  categories_heading: "",
  categories: [],
  benefits_heading: "",
  benefits: [],
  applications_heading: "",
  applications_intro: "",
  applications: [],
  support_heading: "",
  support_intro: "",
  support_items: [],
  faqs_heading: "",
  faqs: [],
  final_cta_heading: "",
  final_cta_body: "",
  final_cta_text: "",
};

function Group({ id, title, openId, setOpenId, children }) {
  const open = openId === id;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 overflow-hidden">
      <button type="button" onClick={() => setOpenId(open ? null : id)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 transition">
        <span className="font-heading font-semibold text-[13.5px] tracking-tight" style={{ color: "#0F2A6E" }}>{title}</span>
        <CaretDown size={13} weight="bold" className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-200 bg-white">{children}</div>}
    </div>
  );
}

function StringListEditor({ value = [], onChange, placeholder, testId }) {
  const update = (idx, v) => { const next = [...value]; next[idx] = v; onChange(next); };
  const add = () => onChange([...value, ""]);
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2" data-testid={testId}>
      {value.map((it, i) => (
        <div key={i} className="flex gap-2">
          <Input value={it} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} className="text-[13px]" />
          <Button type="button" variant="outline" size="sm" onClick={() => remove(i)} className="text-[#1B6FE0] shrink-0"><Trash size={13} weight="bold" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="text-[#1B6FE0]"><Plus size={13} weight="bold" /> Add</Button>
    </div>
  );
}

function KVListEditor({ value = [], onChange, fields, placeholderMap = {}, testId }) {
  // fields: ['title','desc'] or ['q','a'] etc
  const update = (idx, key, v) => { const next = [...value]; next[idx] = { ...(next[idx] || {}), [key]: v }; onChange(next); };
  const add = () => onChange([...value, fields.reduce((m, f) => ({ ...m, [f]: "" }), {})]);
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3" data-testid={testId}>
      {value.map((row, i) => (
        <div key={i} className="p-3 rounded-md border border-slate-200 bg-slate-50/60 relative">
          <button type="button" onClick={() => remove(i)} className="absolute top-2 right-2 text-slate-400 hover:text-[#1B6FE0]">
            <Trash size={13} weight="bold" />
          </button>
          {fields.map((f) => (
            <div key={f} className="mb-2">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-slate-500">{f}</Label>
              {f === "desc" || f === "a" || f === "intro" ? (
                <Textarea rows={2} value={row[f] || ""} onChange={(e) => update(i, f, e.target.value)} placeholder={placeholderMap[f] || ""} className="mt-1 text-[13px]" />
              ) : f === "best_for" ? (
                <StringListEditor value={Array.isArray(row[f]) ? row[f] : []} onChange={(v) => update(i, f, v)} placeholder="e.g. Enterprise office networks" />
              ) : (
                <Input value={row[f] || ""} onChange={(e) => update(i, f, e.target.value)} placeholder={placeholderMap[f] || ""} className="mt-1 text-[13px]" />
              )}
            </div>
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="text-[#1B6FE0]"><Plus size={13} weight="bold" /> Add row</Button>
    </div>
  );
}

export default function BrandDetailEditor({ value, onChange }) {
  const d = { ...EMPTY_DETAIL, ...(value || {}) };
  const set = (k, v) => onChange({ ...d, [k]: v });
  const [openId, setOpenId] = React.useState("hero");

  return (
    <div className="space-y-3" data-testid="brand-detail-editor">
      <div className="flex items-center justify-between">
        <Label>Detail page content</Label>
        <button type="button" onClick={() => onChange(null)} className="text-[11.5px] text-slate-500 hover:text-[#1B6FE0]">
          Clear all (use minimal landing)
        </button>
      </div>

      <Group id="hero" title="Hero" openId={openId} setOpenId={setOpenId}>
        <div><Label>Big H1</Label><Input value={d.hero_h1} onChange={(e) => set("hero_h1", e.target.value)} className="mt-1.5" placeholder="e.g. Ritchfield Structured Cabling & Data Center Solutions" /></div>
        <div><Label>Hero description (newlines preserved)</Label><Textarea rows={4} value={d.hero_desc} onChange={(e) => set("hero_desc", e.target.value)} className="mt-1.5" /></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Primary CTA</Label><Input value={d.cta_primary} onChange={(e) => set("cta_primary", e.target.value)} placeholder="Request Product Details" className="mt-1.5" /></div>
          <div><Label>Secondary CTA</Label><Input value={d.cta_secondary} onChange={(e) => set("cta_secondary", e.target.value)} placeholder="Talk to Our Team" className="mt-1.5" /></div>
        </div>
      </Group>

      <Group id="overview" title="About / Overview" openId={openId} setOpenId={setOpenId}>
        <div><Label>Heading</Label><Input value={d.overview_heading} onChange={(e) => set("overview_heading", e.target.value)} placeholder="About …" className="mt-1.5" /></div>
        <div><Label>Body (newlines preserved)</Label><Textarea rows={5} value={d.overview_body} onChange={(e) => set("overview_body", e.target.value)} className="mt-1.5" /></div>
      </Group>

      <Group id="categories" title={`Product categories (${d.categories.length})`} openId={openId} setOpenId={setOpenId}>
        <div><Label>Section heading</Label><Input value={d.categories_heading} onChange={(e) => set("categories_heading", e.target.value)} placeholder="Product range" className="mt-1.5" /></div>
        <KVListEditor
          value={d.categories}
          onChange={(v) => set("categories", v)}
          fields={["title", "desc", "icon", "best_for"]}
          placeholderMap={{ title: "e.g. Copper Cabling", desc: "Short description…", icon: "lightning · shield · cpu · gauge · solar · lightbulb · factory · cube · wrench · storefront · package · star" }}
          testId="brand-categories"
        />
      </Group>

      <Group id="benefits" title={`Benefits (${d.benefits.length})`} openId={openId} setOpenId={setOpenId}>
        <div><Label>Section heading</Label><Input value={d.benefits_heading} onChange={(e) => set("benefits_heading", e.target.value)} placeholder="Why choose …" className="mt-1.5" /></div>
        <KVListEditor
          value={d.benefits}
          onChange={(v) => set("benefits", v)}
          fields={["title", "desc"]}
          placeholderMap={{ title: "e.g. Reliable Network Performance", desc: "Short benefit description" }}
          testId="brand-benefits"
        />
      </Group>

      <Group id="applications" title={`Applications (${d.applications.length})`} openId={openId} setOpenId={setOpenId}>
        <div><Label>Section heading</Label><Input value={d.applications_heading} onChange={(e) => set("applications_heading", e.target.value)} placeholder="Where … is used" className="mt-1.5" /></div>
        <div><Label>Intro paragraph</Label><Textarea rows={2} value={d.applications_intro} onChange={(e) => set("applications_intro", e.target.value)} className="mt-1.5" /></div>
        <div><Label>Applications</Label><StringListEditor value={d.applications} onChange={(v) => set("applications", v)} placeholder="e.g. Office networks" testId="brand-applications" /></div>
      </Group>

      <Group id="support" title={`OpSys support items (${d.support_items.length})`} openId={openId} setOpenId={setOpenId}>
        <div><Label>Section heading</Label><Input value={d.support_heading} onChange={(e) => set("support_heading", e.target.value)} placeholder="How OpSys supports …" className="mt-1.5" /></div>
        <div><Label>Intro paragraph</Label><Textarea rows={2} value={d.support_intro} onChange={(e) => set("support_intro", e.target.value)} className="mt-1.5" /></div>
        <div><Label>Support items</Label><StringListEditor value={d.support_items} onChange={(v) => set("support_items", v)} placeholder="e.g. Product selection and BOQ support" testId="brand-support" /></div>
      </Group>

      <Group id="faqs" title={`FAQs (${d.faqs.length})`} openId={openId} setOpenId={setOpenId}>
        <div><Label>Section heading</Label><Input value={d.faqs_heading} onChange={(e) => set("faqs_heading", e.target.value)} placeholder="… FAQs" className="mt-1.5" /></div>
        <KVListEditor
          value={d.faqs}
          onChange={(v) => set("faqs", v)}
          fields={["q", "a"]}
          placeholderMap={{ q: "Question?", a: "Answer." }}
          testId="brand-faqs"
        />
      </Group>

      <Group id="final" title="Final CTA" openId={openId} setOpenId={setOpenId}>
        <div><Label>Heading</Label><Input value={d.final_cta_heading} onChange={(e) => set("final_cta_heading", e.target.value)} placeholder="Need … for your project?" className="mt-1.5" /></div>
        <div><Label>Body</Label><Textarea rows={3} value={d.final_cta_body} onChange={(e) => set("final_cta_body", e.target.value)} className="mt-1.5" /></div>
        <div><Label>CTA button text</Label><Input value={d.final_cta_text} onChange={(e) => set("final_cta_text", e.target.value)} placeholder="Send an inquiry" className="mt-1.5" /></div>
      </Group>
    </div>
  );
}
